import type { FC } from 'react';
import { useContext, useMemo } from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { Url, USER_PROFILE_SECTION_PATHS, USER_PROFILE_SHOP_SUB_PATHS } from '@wix/da-url';
import { PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import ThumbStatusIndicator from '@wix/da-ds/pkg/thumbs/ThumbStatusIndicator';
import { ThumbStatusTypes } from '@wix/da-ds/pkg/thumbs/types';
import ChevronRightIcon from '@wix/da-ds/pkg/Icons/16x16/ChevronRight';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import DeviationMediaService from '@wix/da-shared-react/pkg/utils/DeviationMediaService';
import { useTranslation } from 'react-i18next';
import { getProfileOwnerUser } from '../../../../selectors/users';
import { GalleryPromoType } from '../hooks/useGalleryUpsell';

import s from './GalleryUpsellCard.scss';

export interface Props {
  promoType: GalleryPromoType;
  promoCount: number;
  promoDeviation?: PapiDeviation;
  className?: string;
}

const GalleryUpsellCard: FC<Props> = ({ promoType, promoCount, promoDeviation, className }) => {
  const isMobile = useContext(MobileContext);
  const { t } = useTranslation();
  const profileOwner = useSelector(getProfileOwnerUser);

  const isOriginals = promoType === 'originals';

  const href = isOriginals
    ? Url.userShopLink(profileOwner?.username, USER_PROFILE_SHOP_SUB_PATHS.ORIGINALS)
    : Url.userLink(profileOwner?.username, USER_PROFILE_SECTION_PATHS.SUBSCRIPTIONS);

  const titleKey = isOriginals
    ? 'pages.gallections.originalsUpsell.title'
    : 'pages.gallections.tierUpsell.title';

  const subtitleKey = isOriginals
    ? 'pages.gallections.originalsUpsell.subtitle'
    : 'pages.gallections.tierUpsell.subtitle';

  const indicatorType: ThumbStatusTypes = isOriginals
    ? ('originals' as ThumbStatusTypes)
    : 'in-tier';

  const coverSrc = useMemo(() => {
    if (!promoDeviation) {
      return undefined;
    }
    const width = isMobile ? 92 : 257;
    const height = isMobile ? 71 : 104;
    return DeviationMediaService.getSmallestThumbThatFullyCovers(
      promoDeviation,
      width,
      height,
      thumbs => thumbs[thumbs.length - 1]
    )?.src;
  }, [promoDeviation, isMobile]);

  if (isMobile) {
    return (
      <a href={href} className={classnames(s['root'], s['mobile'], className)}>
        <div className={s['inner']}>
          <div className={s['mobile-cover']}>
            {coverSrc ? (
              <img alt="" className={s['cover-image']} src={coverSrc} />
            ) : (
              <div className={s['cover-placeholder']} />
            )}
          </div>
          <div className={s['mobile-content']}>
            <ThumbStatusIndicator type={indicatorType} hasAccess size="medium" />
            <div className={s['mobile-text']}>
              <span className={s['title']}>{t(titleKey)}</span>
              <span className={s['subtitle']}>{t(subtitleKey, { count: promoCount })}</span>
            </div>
          </div>
          <div className={s['mobile-chevron']}>
            <ChevronRightIcon size={IconSize.SMALLER} />
          </div>
        </div>
      </a>
    );
  }

  return (
    <a href={href} className={classnames(s['root'], className)}>
      <div className={s['inner']}>
        <div className={s['cover']}>
          {coverSrc ? (
            <img alt="" className={s['cover-image']} src={coverSrc} />
          ) : (
            <div className={s['cover-placeholder']} />
          )}
        </div>
        <div className={s['info']}>
          <span className={s['title']}>{t(titleKey)}</span>
          <span className={s['subtitle']}>{t(subtitleKey, { count: promoCount })}</span>
        </div>
        <div className={s['indicator']}>
          <ThumbStatusIndicator type={indicatorType} hasAccess size="large" />
        </div>
      </div>
    </a>
  );
};

GalleryUpsellCard.displayName = 'GalleryUpsellCard';
export default GalleryUpsellCard;
